"use client";

import { ReactNode, useState } from "react";
import { cn } from "@plura/shared/utils";
import { plans } from "../../../constants/pricing";
import { Button } from "../../ui/Button";
import { Icon } from "../../ui/Icon";

const DURATIONS = [
  { id: 0, label: "Monthly" },
  { id: 1, label: "Yearly" },
] as const;

/** Compact monthly / yearly segmented control — lives inside each plan card,
 * all synced to the same shared state. */
const BillingToggle = ({
  selected,
  onSelect,
  className,
}: {
  selected: number;
  onSelect: (id: number) => void;
  className?: string;
}) => (
  <div className={cn("bg-shade inline-flex items-center rounded-full p-0.5", className)}>
    {DURATIONS.map((opt) => (
      <button
        key={opt.id}
        type="button"
        onClick={() => onSelect(opt.id)}
        className={cn(
          "rounded-full px-3.5 py-1 font-body text-xs font-semibold transition-colors duration-200",
          selected === opt.id ? "bg-dark text-white" : "text-dark/55 hover:text-dark",
        )}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

const PricingCardsSection = () => {
  const [selectedDuration, setSelectedDuration] = useState(1);
  const duration = selectedDuration === 0 ? "monthly" : "yearly";

  return (
    <section className="landing-container w-full">
      {/* Row of plan cards — full container width with extra side inset */}
      <div className="grid grid-cols-3 items-stretch gap-5 px-[49px] max-lg:grid-cols-1 max-lg:gap-6 max-lg:px-0">
        {plans.map((plan) => (
          <PricingCard
            key={plan.title}
            title={plan.title}
            description={plan.description}
            price={plan.price[duration]}
            features={plan.features.card}
            recommended={plan.title === "Pro"}
            selectedDuration={selectedDuration}
            onSelectDuration={setSelectedDuration}
          />
        ))}
      </div>
    </section>
  );
};

export default PricingCardsSection;

interface PricingCardProps {
  title: string;
  description: string;
  price: number;
  recommended?: boolean;
  selectedDuration: number;
  onSelectDuration: (id: number) => void;
  features: {
    id: string;
    title: string | ReactNode;
    description?: string;
    icon?: string;
  }[];
}

const PricingCard = ({
  title,
  description,
  price,
  recommended,
  features,
  selectedDuration,
  onSelectDuration,
}: PricingCardProps) => {
  const isFree = price === 0;

  return (
    <div
      className={cn(
        "relative flex flex-col rounded-2xl p-6",
        recommended ? "bg-purple" : "border border-[#e5e5e5] bg-white",
      )}
    >
      {/* Billing toggle — top-right corner of the card, synced across all cards */}
      <BillingToggle
        selected={selectedDuration}
        onSelect={onSelectDuration}
        className="absolute right-6 top-6"
      />

      {/* Header — title + description top, price pinned to bottom so prices line up across cards */}
      <div className="flex min-h-[189px] flex-col">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <h3 className="font-heading text-[30px] font-medium leading-9 tracking-[-0.03em] text-dark">
              {title}
            </h3>
            {recommended && (
              <span className="inline-flex items-center rounded-[3px] bg-white px-3 py-1 font-body text-xs font-bold uppercase leading-4 tracking-[0.03em] text-dark">
                Popular
              </span>
            )}
          </div>
          <p className="font-body text-base font-medium leading-6 text-[#555555]">{description}</p>
        </div>

        <p className="mt-auto font-body text-[32px] font-bold capitalize leading-[1.3] text-dark">
          ${price}
          <span className="ml-1 text-base font-medium text-[#555555]">/mo</span>
        </p>
      </div>

      {/* CTA */}
      <Button
        href="https://app.formsuite.co/sign-up"
        variant={recommended ? "primary" : "secondary"}
        size="medium"
        className="mt-5 w-full"
      >
        {isFree ? "Get Started For Free" : "Choose This Plan"}
      </Button>

      {/* Divider */}
      <hr className="my-[30px] border-t border-black/[0.14]" />

      {/* What's included */}
      <div className="flex flex-col gap-3">
        <p className="font-body text-sm leading-[30px] text-[#676767]">What&apos;s included:</p>
        <ul className="flex flex-col gap-2">
          {features.map((feature) => (
            <li key={feature.id} className="flex items-center gap-3">
              <Icon name="check" className="size-4 shrink-0 text-dark" />
              <span className="font-body text-sm font-medium leading-6 text-dark">{feature.title}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
